import React, { useState, useEffect, useCallback } from 'react';

import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import { fetchRequestHistoryV2 } from 'actions/requestHistory';
import { SearchParamsHistory, SearchTypesHistory } from 'components/TripSearch/models';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { RequestedItem, RequestHistoryPayload } from 'models/requestHistory';
import RequestHistory from 'screens/components/RequestHistory';

export const MAX_ITEMS_PER_PAGE = 10;

const Container = () => {
  const { t } = useStoreTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<RequestedItem[]>();
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState();

  const getDates = (active) => {
    const initDate =
      active === SearchTypesHistory.week
        ? `${moment().subtract(1, 'week').format('YYYY-MM-DD')}T00:00:00Z`
        : active === SearchTypesHistory.month
        ? `${moment().subtract(1, 'month').format('YYYY-MM-DD')}T00:00:00Z`
        : undefined;
    const endDate =
      active === SearchTypesHistory.week || active === SearchTypesHistory.month
        ? `${moment().format('YYYY-MM-DD')}T00:00:00Z`
        : undefined;
    return {
      initDate,
      endDate,
    };
  };
  const [payload, setPayload] = useState<RequestHistoryPayload>({
    ...getDates('week'),
    page: 1,
    maxItemsPerPage: MAX_ITEMS_PER_PAGE,
  });
  const [totalItems, setTotalItems] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchParamsHistory>({
    ...getDates('week'),
    page: 1,
    type: SearchTypesHistory.week,
  });

  const updatePayload = (params: SearchParamsHistory) => {
    const { initDate, endDate } = params.type === SearchTypesHistory.search ? params : getDates(params.type);
    const page = params.page ?? 1;
    const searchExtraParams = (isPayload: boolean) => {
      const baseParams = {
        distanceRestriction: params.type === SearchTypesHistory.search ? params.distanceRestriction : undefined,
        destination: params.type === SearchTypesHistory.search ? params.destination : undefined,
        travelStatus: params.type === SearchTypesHistory.search ? params.travelStatus : undefined,
        travelItineraryId: params.type === SearchTypesHistory.search ? params.travelItineraryId : undefined,
      };
      return !!isPayload
        ? {
            ...baseParams,
            travelType: params.type === SearchTypesHistory.search ? params.tripType : undefined,
          }
        : {
            ...baseParams,
            tripType: params.type === SearchTypesHistory.search ? params.tripType : undefined,
          };
    };
    setCurrentPage(page);
    setSearchParams({ ...searchParams, ...params, initDate, endDate, page, ...searchExtraParams(false) });
    setPayload({
      initDate,
      endDate,
      page,
      maxItemsPerPage: MAX_ITEMS_PER_PAGE,
      ...searchExtraParams(true),
    });
    setLoaded(false);
    setError(undefined);
  };

  const fetchHistory = useCallback(
    async (payload) => {
      setIsLoading(true);
      try {
        const response = await fetchRequestHistoryV2(payload);
        const { data } = response;
        let travels: RequestedItem[] = [];
        let total = 0;
        if (!isEmpty(data)) {
          travels = data.data.travels;
          total = data.data.total;
        }
        if (travels) setData(travels);
        setTotalItems(total);
        if (total === 0 || travels?.length === 0) setError(t('dashboard.emptyMessage'));
      } catch (e) {
        setError(t('defaultError.default'));
      } finally {
        setLoaded(true);
        setIsLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (!loaded && !isLoading) {
      fetchHistory(payload);
    }
  }, [fetchHistory, loaded, payload, isLoading]);

  return (
    <RequestHistory
      data={data}
      isLoading={isLoading}
      loaded={loaded}
      setPayload={updatePayload}
      total={totalItems}
      maxItemsPerPage={MAX_ITEMS_PER_PAGE}
      params={searchParams}
      page={currentPage}
      error={error}
    />
  );
};

export default Container;
